import { useState, useEffect, useRef, useLayoutEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';

// Model //
import FetchModule from '../Network/FetchModule.js';
import LocalStorage from '../Network/LocalStorage.js';
// Model //

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

// Component //
import AccountInput from '../../../Component/AccountComponent/AccountInput.js';
import AccountPasswordInput from '../../../Component/AccountComponent/AccountPasswordInput.js';
import MultiLoginAccountView from '../../../Component/Normal/MultiLoginAccountView.js';
// Component //

// Image //
import PlusButtonIcon from '../../../image/PlusButton.png';
import UpArrow from '../../../image/Left.png';
// Image //

export default function MultiLogin(props) {
  const { storeName } = props;
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  // 부계정(SUB)은 계정 전환 불가 — CanSwitchAccount=false면 전환 토글 숨김 (미설정/MAIN은 허용) //
  const canSwitchAccount = window.sessionStorage.getItem('CanSwitchAccount') !== 'false';

  const [isOpenMultiLoginView, setIsOpenMultiLoginView] = useState(false);
  const [isOpenAddMultiLoginView, setIsOPenAddMultiLoginView] = useState(false);

  const [accounts, setAccounts] = useState([]);
  const [idClickCount, setIdClickCount] = useState(0);
  const [pwClickCount, setPwClickCount] = useState(0);

  const openAreaRef = useRef(null);
  const [openAreaMaxHeight, setOpenAreaMaxHeight] = useState(0);

  const addAreaRef = useRef(null);
  const IdRef = useRef(null);
  const PWRef = useRef(null);

  const fetchModule = useMemo(() => new FetchModule(), []);

  // 계정 목록 새로고침 함수
  const refreshAccountList = useCallback(async () => {
    const responseData = await fetchModule.getDataVersion2('account/switch/list', 'GET');

    if (responseData.status === 200) {
      setAccounts(responseData.data);
    }
  }, [fetchModule]);

  // 입력 필드 리셋 함수
  const resetInputFields = useCallback(() => {
    setIdClickCount(0);
    setPwClickCount(0);

    IdRef.current?.setValue('');
    PWRef.current?.setValue('');

    IdRef.current?.reset(t('multiLogin.idPlaceholder'));
    PWRef.current?.reset(t('multiLogin.pwPlaceholder'));
  }, []);

  useLayoutEffect(() => {
    const el = openAreaRef.current;

    if (!el) return;

    if (!isOpenMultiLoginView) {
      setOpenAreaMaxHeight(0);

      return;
    }

    const TARGET_ADD_HEIGHT = isOpenAddMultiLoginView ? 245 : 0;

    const measure = () => {
      const currentAddH = addAreaRef.current ? addAreaRef.current.getBoundingClientRect().height : 0;

      const addDelta = Math.max(0, TARGET_ADD_HEIGHT - currentAddH);

      setOpenAreaMaxHeight(el.scrollHeight + addDelta);
    };

    measure();

    const raf1 = requestAnimationFrame(measure);
    const raf2 = requestAnimationFrame(measure);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [isOpenMultiLoginView, accounts.length, isOpenAddMultiLoginView]);

  useEffect(() => {
    if (isOpenAddMultiLoginView || !isOpenMultiLoginView) return;

    const addEl = addAreaRef.current;
    const el = openAreaRef.current;
    if (!addEl || !el) return;

    const onTransitionEnd = (e) => {
      if (e.propertyName === 'height') {
        setOpenAreaMaxHeight(el.scrollHeight);
      }
    };

    addEl.addEventListener('transitionend', onTransitionEnd);
    return () => addEl.removeEventListener('transitionend', onTransitionEnd);
  }, [isOpenAddMultiLoginView, isOpenMultiLoginView]);

  useEffect(() => {
    if (isOpenMultiLoginView) {
      refreshAccountList();
    }
  }, [isOpenMultiLoginView, refreshAccountList]);

  useEffect(() => {
    if (idClickCount === 0 && pwClickCount === 0) return;

    if (IdRef.current?.getValue() === '' && idClickCount > 0) {
      IdRef.current?.setDefaultPlaceholder(t('multiLogin.invalidId'));
    }

    if (PWRef.current?.getValue() === '' && pwClickCount > 0) {
      PWRef.current?.setDefaultPlaceholder(t('multiLogin.invalidPw'));
    }
  }, [idClickCount, pwClickCount]);

  function onOpen() {
    setIsOpenMultiLoginView(true);
  }

  function onClose() {
    setIsOpenMultiLoginView(false);
    setIsOPenAddMultiLoginView(false);
  }

  function onOpenAdd() {
    setIsOPenAddMultiLoginView(true);
  }

  function onCloseAdd() {
    setIsOPenAddMultiLoginView(false);
    resetInputFields();
  }

  function clickCountFn(className, clickCount) {
    if (className === 'IdInput') {
      setIdClickCount(clickCount);
    }
    else if (className === 'PWInput') {
      setPwClickCount(clickCount);
    }
  }

  async function onAddAccount() {
    const userId = IdRef.current?.getValue();
    const userPw = PWRef.current?.getValue();

    let hasError = false;

    if (!userId || userId === '') {
      IdRef.current?.setDefaultPlaceholder(t('multiLogin.invalidId'));
      IdRef.current?.plusClickCount();

      hasError = true;
    }

    if (!userPw || userPw === '') {
      PWRef.current?.setDefaultPlaceholder(t('multiLogin.invalidPw'));
      PWRef.current?.plusClickCount();

      hasError = true;
    }

    if (hasError) {
      return;
    }

    const responseData = await fetchModule.postDataVersion2('account/switch/register', 'POST', {
      account: userId,
      password: userPw
    });

    if (responseData.status === 200) {
      resetInputFields();
      setIsOPenAddMultiLoginView(false);

      await refreshAccountList();
    }
    else {
    alert(responseData.error);
    }
  }

  function onClickSignup() {
    window.location.href = 'https://link-zone.io/';
  }

  async function onSwitchAccount(accountId) {
    const responseData = await fetchModule.postDataVersion2('account/switch/execute', 'POST', {
      account: accountId
    });

    if (responseData.status === 200) {
      window.sessionStorage.setItem('Token', 'Bearer ' + responseData.data.token);
      window.sessionStorage.setItem('RefreshToken', 'Bearer ' + responseData.data.refreshToken);
      window.sessionStorage.setItem('StoreId', responseData.data.storeId);

      // 전환 대상 계정 유형/네비 동기화 (전환 응답 제공 시 반영, 미제공 시 이전 부계정 값 잔류 방지) //
      if (responseData.data.accountType) window.sessionStorage.setItem('AccountType', responseData.data.accountType);
      else window.sessionStorage.removeItem('AccountType');

      if (responseData.data.canSwitchAccount !== undefined) window.sessionStorage.setItem('CanSwitchAccount', responseData.data.canSwitchAccount);
      else window.sessionStorage.removeItem('CanSwitchAccount');

      if (responseData.data.accountType === 'SUB' && Array.isArray(responseData.data.navItems)) window.sessionStorage.setItem('NavItems', JSON.stringify(responseData.data.navItems));
      else window.sessionStorage.removeItem('NavItems');

      LocalStorage.saveSession();

      window.location.reload();
    }
    else {
      alert(responseData.error);
    }
  }

  async function onDeleteAccount(e, accountId) {
    e.stopPropagation();

    const responseData = await fetchModule.postDataVersion2('account/switch/delete', 'POST', {
      account: accountId
    });

    if (responseData.status === 200) {
      alert(t('multiLogin.accountDeleted'));
      await refreshAccountList();
    }
    else {
      alert(responseData.error);
    }
  }

  return (
    <TopLevelWrapper>
      <MultiLoginView isOpen={isOpenMultiLoginView}>
        <MultiLoginDefaultWrapper>
          <MultiLoginTextWrapper>
            <Text fontFamily="NotoSansKR-Medium" fontSize={18} color={theme.gymName} ellipsis>{storeName}</Text>
          </MultiLoginTextWrapper>
          <ControllerButton onClick={isOpenMultiLoginView ? onClose : onOpen} isOpen={isOpenMultiLoginView} $hidden={!canSwitchAccount}>
            <PlusButton src={PlusButtonIcon} isOpen={isOpenMultiLoginView} />
          </ControllerButton>
        </MultiLoginDefaultWrapper>
        <MultiLoginOpenWrapper ref={openAreaRef} isOpen={isOpenMultiLoginView} maxHeight={openAreaMaxHeight}>
          {
            accounts.map((account, index) => (
              <MultiLoginAccountView 
                key={'Account-' + index} 
                data={account}
                onSwitchAccount={onSwitchAccount}
                onDeleteAccount={onDeleteAccount}/>
            ))
          }
          <AddAccountWrapper>
            <AddAccountView ref={addAreaRef} isOpen={isOpenAddMultiLoginView}>
              <TopWrapper>
                <Text fontFamily="NotoSansKR-Medium" fontSize={16} color={theme.text}>{t('multiLogin.idLabel')}</Text>
                <ColoseButton src={UpArrow} onClick={onCloseAdd} />
              </TopWrapper>
              <AccountInput ref={IdRef} className='IdInput' placeholder={t('multiLogin.idPlaceholder')} type='text' clickCountFn={clickCountFn} onClickMultiLogin={onAddAccount} />
              <AddAccountGap />
              <Text fontFamily="NotoSansKR-Medium" fontSize={16} color={theme.text}>{t('multiLogin.pwLabel')}</Text>
              <AccountPasswordInput ref={PWRef} className='PWInput' placeholder={t('multiLogin.pwPlaceholder')} type='password' clickCountFn={clickCountFn} onClickMultiLogin={onAddAccount} />
              <AddAccountGap />
              <BottomWrapper>
                <Text fontFamily="NotoSansKR-Regular" fontSize={16} color={theme.subText}>{t('multiLogin.newUser')}</Text>
                <Text fontFamily="NotoSansKR-Regular" fontSize={16} color={theme.primary} cursor="pointer" onClick={onClickSignup}>{t('multiLogin.signup')}</Text>
              </BottomWrapper>
            </AddAccountView>
            <AddAccountButton onClick={isOpenAddMultiLoginView ? onAddAccount : onOpenAdd}>
              {isOpenAddMultiLoginView && <Text fontFamily="NotoSansKR-Medium" fontSize={16} color={theme.gymName}>{t('multiLogin.addAccount')}</Text>}
              <PlusButton src={PlusButtonIcon} />
            </AddAccountButton>
          </AddAccountWrapper>
        </MultiLoginOpenWrapper>
      </MultiLoginView>
      <ModalOverlay isOpen={isOpenMultiLoginView} onClick={onClose} />
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled.div`
  position: relative;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.2);

  z-index: 999;

  opacity: ${props => (props.isOpen ? 1 : 0)};

  pointer-events: ${props => (props.isOpen ? 'auto' : 'none')};

  transition: opacity 0.3s ease-in-out;
`;

const MultiLoginView = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  padding: 0 0;

  width: 396px;
  min-height: 45px;

  border-radius: ${props => (props.isOpen ? '20px' : '30px')};
  background: ${props => props.theme.primaryGradient};

  z-index: 1000;

  transition: border-radius 0.5s ease-in-out;
`;

const MultiLoginDefaultWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  width: 100%;
  min-height: 45px;
`;

const MultiLoginTextWrapper = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  width: 310px;
`;

const ControllerButton = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  margin-right: 10px;

  width: 30px;
  height: 30px;

  border-radius: 30px;

  background-color: ${props => (props.isOpen ? 'transparent' : '#E57F44')};

  opacity: ${props => props.$hidden ? '0' : '1'};
  pointer-events: ${props => props.$hidden ? 'none' : 'auto'};

  transition: background-color 0.2s ease-in-out;

  &:hover {
    cursor: pointer;
    background-color: #E57F44;
  }
`;

const PlusButton = styled.img`
  width: 23px;
  height: 23px;

  transform: rotate(${props => (props.isOpen ? '45deg' : '0deg')});
  transition: transform 0.3s ease-in-out;
`;

const MultiLoginOpenWrapper = styled.div`
  display: flex;
  flex-direction: column;

  gap: 8px;
  margin-top: ${props => (props.isOpen ? '10px' : '0')};

  max-height: ${props => (props.isOpen ? `${props.maxHeight}px` : '0px')};
  width: 100%;

  box-sizing: border-box;

  overflow: hidden;

  opacity: ${props => (props.isOpen ? 1 : 0)};
  transform: ${props => (props.isOpen ? 'translateY(0px)' : 'translateY(-6px)')};

  transition:
    max-height 0.35s ease-in-out,
    opacity 0.2s ease-in-out,
    transform 0.25s ease-in-out,
    margin-top 0.35s ease-in-out,
    padding-bottom 0.35s ease-in-out;

  pointer-events: ${props => (props.isOpen ? 'auto' : 'none')};
`;

const AddAccountWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;
`;

const AddAccountView = styled.div`
  display: flex;
  flex-direction: column;

  padding: ${props => (props.isOpen ? '15px' : '0 15px')};

  width: 100%;
  height: ${props => (props.isOpen ? `245px` : '0px')};

  box-sizing: border-box;

  background-color: ${props => props.theme.subBackground};
  overflow: hidden;

  transform: ${props => (props.isOpen ? 'translateY(0px)' : 'translateY(-6px)')};

  transition:
    height 0.35s ease-in-out,
    transform 0.35s ease-in-out,
    padding-top 0.35s ease-in-out,
    padding-bottom 0.35s ease-in-out;

  pointer-events: ${props => (props.isOpen ? 'auto' : 'none')};
`;

const AddAccountGap = styled.div`
  height: 25px;
`;

const AddAccountButton = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  gap: 10px;

  height: 45px;

  cursor: pointer;
`;

const TopWrapper = styled.div`
  display: flex;
  
  justify-content: space-between;
  align-items: center;
`;

const BottomWrapper = styled.div`
  display: flex;

  justify-content: flex-start;
  align-items: center;

  gap: 10px;
`;

const ColoseButton = styled.img`
  width: 211x;
  height: 16px;

  transform: rotate(90deg);
  cursor: pointer;
`;

const Text = styled.div`
  margin: 0 0 0 0;

  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  ${props => props.ellipsis && `
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  `}

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: ${props => props.cursor};
  }
`;
